import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import styled from 'styled-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const ChartsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
  gap: 20px;
  margin-top: 30px;
`;

const ChartCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
`;

const ChartTitle = styled.h3`
  margin: 0 0 20px 0;
  color: #333;
  font-size: 1.2rem;
  font-weight: 600;
  text-align: center;
`;

const DebugInfo = styled.div`
  background: #f0f0f0;
  padding: 10px;
  margin: 10px 0;
  border-radius: 5px;
  font-size: 0.8rem;
  color: #666;
`;

interface ChartsProps {
  clubs: any[];
  athletes: any[];
  pendingClubs: any[];
  pendingAthletes: any[];
}

const Charts: React.FC<ChartsProps> = ({ clubs, athletes, pendingClubs, pendingAthletes }) => {
  // Debug: Afficher les données reçues
  console.log('Charts - Données reçues:', {
    clubs: clubs.length,
    athletes: athletes.length,
    pendingClubs: pendingClubs.length,
    pendingAthletes: pendingAthletes.length
  });



  // Fonction pour grouper les données par mois
  const groupByMonth = (data: any[]) => {
    const months: { [key: string]: number } = {};
    const monthNames = [
      'Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun',
      'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'
    ];

    // Si pas de données, créer des données par défaut pour le mois actuel
    if (data.length === 0) {
      const now = new Date();
      const currentMonth = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
      months[currentMonth] = 0;
      return months;
    }

    data.forEach(item => {
      try {
        const date = new Date(item.dateCreation);
        if (isNaN(date.getTime())) return; // Ignorer les dates invalides
        
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        const monthLabel = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
        
        if (!months[monthLabel]) {
          months[monthLabel] = 0;
        }
        months[monthLabel]++;
      } catch (error) {
        console.warn('Date invalide:', item.dateCreation);
      }
    });

    // Si toujours pas de données, créer des données par défaut
    if (Object.keys(months).length === 0) {
      const now = new Date();
      const currentMonth = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
      months[currentMonth] = 0;
    }

    // Trier les mois chronologiquement
    const sortedMonths: { [key: string]: number } = {};
    Object.keys(months)
      .sort((a, b) => {
        const [monthA, yearA] = a.split(' ');
        const [monthB, yearB] = b.split(' ');
        const monthIndexA = monthNames.indexOf(monthA);
        const monthIndexB = monthNames.indexOf(monthB);
        return parseInt(yearA) - parseInt(yearB) || monthIndexA - monthIndexB;
      })
      .forEach(key => {
        sortedMonths[key] = months[key];
      });

    return sortedMonths;
  };

  // Données pour les inscriptions par mois
  const clubsByMonth = groupByMonth(clubs);
  const athletesByMonth = groupByMonth(athletes);

  // Données pour le graphique en ligne des inscriptions
  const inscriptionData = {
    labels: Object.keys(clubsByMonth).length > 0 ? Object.keys(clubsByMonth) : ['Aucune donnée'],
    datasets: [
      {
        label: 'Clubs',
        data: Object.values(clubsByMonth).length > 0 ? Object.values(clubsByMonth) : [0],
        borderColor: '#2E7D32',
        backgroundColor: 'rgba(46, 125, 50, 0.1)',
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Athlètes',
        data: Object.values(athletesByMonth).length > 0 ? Object.values(athletesByMonth) : [0],
        borderColor: '#1976D2',
        backgroundColor: 'rgba(25, 118, 210, 0.1)',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  // Données pour le graphique en barres des statuts
  const statusData = {
    labels: ['Approuvés', 'En attente', 'Rejetés'],
    datasets: [
      {
        label: 'Clubs',
        data: [
          clubs.length,
          pendingClubs.length,
          clubs.filter(c => c.statut === 'rejete').length
        ],
        backgroundColor: [
          'rgba(46, 125, 50, 0.8)',
          'rgba(255, 193, 7, 0.8)',
          'rgba(244, 67, 54, 0.8)'
        ],
        borderColor: [
          '#2E7D32',
          '#FFC107',
          '#F44336'
        ],
        borderWidth: 1,
      },
      {
        label: 'Athlètes',
        data: [
          athletes.length,
          pendingAthletes.length,
          athletes.filter(a => a.statut === 'rejete').length
        ],
        backgroundColor: [
          'rgba(25, 118, 210, 0.8)',
          'rgba(255, 152, 0, 0.8)',
          'rgba(233, 30, 99, 0.8)'
        ],
        borderColor: [
          '#1976D2',
          '#FF9800',
          '#E91E63'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Données pour le graphique en donut des répartitions
  const repartitionData = {
    labels: ['Clubs Approuvés', 'Athlètes Approuvés', 'Clubs en Attente', 'Athlètes en Attente'],
    datasets: [
      {
        data: [
          clubs.length || 0,
          athletes.length || 0,
          pendingClubs.length || 0,
          pendingAthletes.length || 0
        ],
        backgroundColor: [
          '#2E7D32',
          '#1976D2',
          '#FFC107',
          '#FF9800'
        ],
        borderColor: [
          '#1B5E20',
          '#1565C0',
          '#FF8F00',
          '#F57C00'
        ],
        borderWidth: 2,
      },
    ],
  };

  // Données pour le graphique des rejets
  const rejetsData = {
    labels: ['Clubs Rejetés', 'Athlètes Rejetés'],
    datasets: [
      {
        data: [
          clubs.filter(c => c.statut === 'rejete').length || 0,
          athletes.filter(a => a.statut === 'rejete').length || 0
        ],
        backgroundColor: [
          'rgba(244, 67, 54, 0.8)',
          'rgba(233, 30, 99, 0.8)'
        ],
        borderColor: [
          '#F44336',
          '#E91E63'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Données pour le graphique des inscriptions par ville
  const getVilleData = () => {
    const villeStats: { [key: string]: { clubs: number; athletes: number } } = {};
    
    clubs.forEach(club => {
      if (!villeStats[club.ville]) {
        villeStats[club.ville] = { clubs: 0, athletes: 0 };
      }
      villeStats[club.ville].clubs++;
    });
    
    athletes.forEach(athlete => {
      if (!villeStats[athlete.situationGeographique]) {
        villeStats[athlete.situationGeographique] = { clubs: 0, athletes: 0 };
      }
      villeStats[athlete.situationGeographique].athletes++;
    });

    // Si pas de données, créer des données par défaut
    if (Object.keys(villeStats).length === 0) {
      return {
        labels: ['Aucune donnée'],
        datasets: [
          {
            label: 'Clubs',
            data: [0],
            backgroundColor: 'rgba(46, 125, 50, 0.8)',
            borderColor: '#2E7D32',
            borderWidth: 1,
          },
          {
            label: 'Athlètes',
            data: [0],
            backgroundColor: 'rgba(25, 118, 210, 0.8)',
            borderColor: '#1976D2',
            borderWidth: 1,
          },
        ],
      };
    }

    const sortedVilles = Object.entries(villeStats)
      .sort(([,a], [,b]) => (b.clubs + b.athletes) - (a.clubs + a.athletes))
      .slice(0, 8); // Top 8 villes

    return {
      labels: sortedVilles.map(([ville]) => ville),
      datasets: [
        {
          label: 'Clubs',
          data: sortedVilles.map(([, stats]) => stats.clubs),
          backgroundColor: 'rgba(46, 125, 50, 0.8)',
          borderColor: '#2E7D32',
          borderWidth: 1,
        },
        {
          label: 'Athlètes',
          data: sortedVilles.map(([, stats]) => stats.athletes),
          backgroundColor: 'rgba(25, 118, 210, 0.8)',
          borderColor: '#1976D2',
          borderWidth: 1,
        },
      ],
    };
  };

  const villeData = getVilleData();

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  const lineOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const barOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  const donutOptions = {
    ...chartOptions,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  return (
    <ChartsContainer>
         <ChartCard>
          <ChartTitle>Évolution des Inscriptions</ChartTitle>
          <div style={{ height: '300px' }}>
            <Line data={inscriptionData} options={lineOptions} />
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Répartition par Statut</ChartTitle>
          <div style={{ height: '300px' }}>
            <Bar data={statusData} options={barOptions} />
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Répartition Générale</ChartTitle>
          <div style={{ height: '300px' }}>
            <Doughnut data={repartitionData} options={donutOptions} />
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Analyse des Rejets</ChartTitle>
          <div style={{ height: '300px' }}>
            <Bar data={rejetsData} options={barOptions} />
          </div>
        </ChartCard>

        <ChartCard>
          <ChartTitle>Inscriptions par Ville (Top 8)</ChartTitle>
          <div style={{ height: '300px' }}>
            <Bar data={villeData} options={barOptions} />
          </div>
                 </ChartCard>
       </ChartsContainer>
   );
 };

export default Charts;
